/**
 * #55 — PWA Install Banner.
 *
 * Listens for the `beforeinstallprompt` event (Chrome/Edge/Android
 * Chrome) and surfaces a dismissible bottom-of-screen banner that
 * invokes the native install prompt on tap. iOS Safari does NOT emit
 * `beforeinstallprompt`; for iOS Safari users on a tech-role session
 * we show a one-time inline hint with the manual Add-to-Home-Screen
 * steps.
 *
 * Scope notes:
 *   • Banner shows on MOBILE breakpoints only (<768px). Dispatchers /
 *     office users on desktop don't need it.
 *   • Dismissals persist in localStorage for 30 days so the banner
 *     doesn't nag.
 *   • Service Worker reintroduction: SHIPPED in iter184 behind the
 *     `REACT_APP_PWA_ENABLED` flag. When the flag is on, installing
 *     the PWA gives users a home-screen icon, standalone window, AND
 *     full offline shell caching via the v3 SW.
 */
import { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const STORAGE_KEY = "pwa_install_dismissed_at";
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isMobile() {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768 || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return isIOS && isSafari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Either the standalone media query (Chrome/Edge) or the legacy iOS
  // Safari `navigator.standalone` flag.
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    Boolean(window.navigator?.standalone)
  );
}

function dismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch { return false; }
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (!isMobile() || isStandalone() || dismissedRecently()) return;

    // Chrome/Edge/Android Chrome path.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setHidden(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari path — no native prompt; just show the manual hint.
    if (isIOSSafari()) {
      setShowIOSHint(true);
      setHidden(false);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* private mode */ }
    setHidden(true);
    setDeferredPrompt(null);
    setShowIOSHint(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        // The browser will fire `appinstalled` next; nothing more to do.
        dismiss();
      } else {
        // User declined — bury the banner for 30 days like a hard
        // dismiss so we don't pester them every page load.
        dismiss();
      }
    } catch {
      dismiss();
    }
  };

  if (hidden) return null;

  // iOS Safari hint — manual Add-to-Home-Screen steps.
  if (showIOSHint && !deferredPrompt) {
    return (
      <div
        className="fixed bottom-3 left-3 right-3 z-50 md:hidden bg-slate-900 text-white rounded-xl shadow-xl p-4 flex items-start gap-3"
        data-testid="pwa-install-banner-ios"
      >
        <Share className="w-5 h-5 shrink-0 mt-0.5 text-sky-300" />
        <div className="flex-1 text-sm">
          <div className="font-semibold mb-0.5">Install ServiceOps</div>
          <div className="text-xs text-slate-300">
            Tap the <Share className="w-3 h-3 inline-block mx-0.5" /> Share icon below, then
            <span className="font-semibold"> Add to Home Screen</span>.
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-white shrink-0"
          aria-label="Dismiss Install Banner"
          data-testid="btn-dismiss-install-banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Chrome/Edge/Android path — native prompt button.
  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-50 md:hidden bg-slate-900 text-white rounded-xl shadow-xl p-4 flex items-center gap-3"
      data-testid="pwa-install-banner"
    >
      <Download className="w-5 h-5 shrink-0 text-emerald-400" />
      <div className="flex-1 text-sm">
        <div className="font-semibold">Install ServiceOps</div>
        <div className="text-xs text-slate-300">Get a home-screen icon and full-screen view.</div>
      </div>
      <button
        onClick={install}
        className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs px-3 py-2 rounded-lg shrink-0"
        data-testid="btn-install-pwa"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        className="text-slate-400 hover:text-white shrink-0 ml-1"
        aria-label="Dismiss Install Banner"
        data-testid="btn-dismiss-install-banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
